/**
 * Utility functions for parsing and formatting user names
 * Provides consistent name handling across the application
 */

/**
 * Parses a displayName into firstName and lastName components
 * @param {string} displayName - The full display name to parse
 * @returns {Object} Object with firstName and lastName properties
 */
export const parseDisplayName = (displayName) => {
  if (!displayName || typeof displayName !== 'string') {
    return { firstName: '', lastName: '' };
  }
  
  const trimmedName = displayName.trim();
  if (!trimmedName) {
    return { firstName: '', lastName: '' };
  }
  
  const parts = trimmedName.split(/\s+/); // Split on any whitespace
  const firstName = parts[0] || '';
  const lastName = parts.slice(1).join(' ') || '';
  
  return { firstName, lastName };
};

/**
 * Formats firstName and lastName into a displayName
 * @param {string} firstName - The first name
 * @param {string} lastName - The last name
 * @returns {string} The formatted display name
 */
export const formatDisplayName = (firstName, lastName) => {
  const first = (firstName || '').trim();
  const last = (lastName || '').trim();
  
  if (!first && !last) {
    return '';
  }
  
  if (!first) {
    return last;
  }
  
  if (!last) {
    return first;
  }
  
  return `${first} ${last}`;
};

/**
 * Gets the user's initials from their displayName
 * @param {string} displayName - The full display name
 * @returns {string} The user's initials (up to 2 characters)
 */
export const getInitials = (displayName) => {
  const { firstName, lastName } = parseDisplayName(displayName);
  
  const firstInitial = firstName.charAt(0).toUpperCase();
  const lastInitial = lastName.charAt(0).toUpperCase();
  
  if (firstInitial && lastInitial) {
    return `${firstInitial}${lastInitial}`;
  }
  
  return firstInitial || lastInitial || 'U';
};

/**
 * Validates that a name contains only allowed characters
 * @param {string} name - The name to validate
 * @returns {boolean} True if the name is valid
 */
export const validateName = (name) => {
  if (!name || typeof name !== 'string') {
    return false;
  }
  
  const trimmedName = name.trim();
  if (!trimmedName) {
    return false;
  }
  
  // Allow letters, spaces, hyphens, apostrophes, and periods
  const nameRegex = /^[a-zA-Z\s\-'.]+$/;
  return nameRegex.test(trimmedName);
};